import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, Clock, Euro, Eye, FileText, Printer, ChevronDown, Building2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { formatMoney, getPaymentMethod } from './constants';
import { toast } from 'sonner';

export default function SouscriptionsTab({
  expandedIds,
  filtered,
  getAmount,
  getMode,
  getName,
  handlePrintRecuContributionAcquitte,
  isPaid,
  setActiveTab,
  setSelectedDoc,
  toggleExpand
}) {
  return (
    <>
      {/* KPIs compactes */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
          <FileText className="h-4 w-4 text-indigo-500 shrink-0" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Total</p>
            <p className="text-lg font-bold text-gray-900">{filtered.length}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
          <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Payees</p>
            <p className="text-lg font-bold text-emerald-700">{filtered.filter(d => isPaid(d)).length}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
          <Clock className="h-4 w-4 text-amber-500 shrink-0" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">En attente</p>
            <p className="text-lg font-bold text-amber-700">{filtered.filter(d => !isPaid(d)).length}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-white">
          <Euro className="h-4 w-4 text-blue-500 shrink-0" />
          <div>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider">Montant total</p>
            <p className="text-lg font-bold text-blue-700">{formatMoney(filtered.reduce((s, d) => s + getAmount(d), 0))} EUR</p>
          </div>
        </div>
      </div>

      {/* Count */}
      <div className="flex items-center gap-3 px-1">
        <span className="text-sm text-gray-500">{filtered.length} souscription(s)</span>
      </div>

      {/* Liste */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center">
          <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-700 font-medium">Aucune souscription trouvee</p>
          <p className="text-sm text-gray-400">Modifiez vos filtres pour voir plus de resultats</p>
        </div>
      ) : (
        <div className="space-y-1" data-testid="souscriptions-fin-list">
          {filtered.map((doc, idx) => {
            const isExpanded = expandedIds.has(`sous-${doc.reference}`);
            const paid = isPaid(doc);
            const method = getPaymentMethod(getMode(doc));
            const MethodIcon = method.icon;
            return (
              <div
                key={doc.reference}
                className={cn(
                  "rounded-lg border transition-all duration-150",
                  isExpanded ? "border-indigo-200 shadow-sm bg-white" : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                )}
                data-testid={`sous-fin-row-${idx}`}
              >
                {/* Compact row */}
                <button onClick={() => toggleExpand(`sous-${doc.reference}`)} className="flex items-center gap-3 px-4 py-2.5 w-full text-left" data-testid={`sous-fin-toggle-${idx}`}>
                  <FileText className={cn("h-4 w-4 shrink-0", paid ? "text-emerald-500" : "text-amber-500")} />
                  <div className="min-w-0 flex-1">
                    <p className={cn("font-medium text-sm truncate", isExpanded ? "text-indigo-700" : "text-gray-900")}>{getName(doc)}</p>
                    <p className="text-[10px] text-gray-400 font-mono truncate">{doc.reference}</p>
                  </div>
                  {paid ? (
                    <Badge className="bg-emerald-700 text-white text-[10px] shrink-0 font-semibold"><CheckCircle className="h-3 w-3 mr-0.5" />Paye</Badge>
                  ) : (
                    <Badge className="bg-amber-600 text-white text-[10px] shrink-0 font-semibold"><Clock className="h-3 w-3 mr-0.5" />En attente</Badge>
                  )}
                  <Badge variant="outline" className={cn("text-[10px] shrink-0 hidden sm:inline-flex", method.color)}>
                    <MethodIcon className="h-3 w-3 mr-0.5" />{method.label}
                  </Badge>
                  <div className="hidden md:flex items-center gap-1 text-xs text-gray-500 shrink-0">
                    <Building2 className="h-3 w-3" />
                    <span className="font-mono">{doc.siret ? doc.siret.slice(0, 9) + '...' : '-'}</span>
                  </div>
                  <span className="text-sm font-bold text-gray-900 tabular-nums shrink-0">{formatMoney(getAmount(doc))} EUR</span>
                  <ChevronDown className={cn("h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200", isExpanded && "rotate-180 text-indigo-500")} />
                </button>

                {/* Expanded */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-gray-100 animate-in fade-in slide-in-from-top-1 duration-150" data-testid={`sous-fin-details-${idx}`}>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Souscripteur</h4>
                        <div className="space-y-1.5 text-sm">
                          <p className="font-medium text-gray-900">{getName(doc)}</p>
                          <div className="text-gray-600 flex items-center gap-1.5">
                            <Building2 className="h-3.5 w-3.5 text-gray-400" /> SIRET: <span className="font-mono text-xs">{doc.siret || 'N/A'}</span>
                          </div>
                          <p className="text-gray-500 text-xs truncate">{doc.email || 'Email non renseigne'}</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Details</h4>
                        <div className="space-y-1.5 text-sm">
                          <div className="flex justify-between"><span className="text-gray-500">Reference:</span><span className="font-mono text-xs">{doc.reference}</span></div>
                          <div className="flex justify-between"><span className="text-gray-500">Montant:</span><span className="font-bold text-gray-900">{formatMoney(getAmount(doc))} EUR</span></div>
                          <div className="flex justify-between"><span className="text-gray-500">Mode:</span><Badge variant="outline" className="text-[10px] capitalize">{getMode(doc)?.replace('_', ' ') || 'Virement'}</Badge></div>
                          <div className="flex justify-between"><span className="text-gray-500">Statut:</span>
                            {paid ? <Badge className="bg-emerald-700 text-white text-[10px] font-semibold">Paye</Badge> : <Badge className="bg-amber-600 text-white text-[10px] font-semibold">En attente</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Actions</h4>
                        <div className="flex flex-wrap gap-1.5">
                          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" data-testid={`sous-fin-view-${idx}`}
                            onClick={(e) => { e.stopPropagation(); setSelectedDoc(doc); setActiveTab('paiements'); toast.info(`Details de ${getName(doc)} - Ref: ${doc.reference}`); }}
                          ><Eye className="h-3.5 w-3.5" /> Voir details</Button>
                          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid={`sous-fin-print-${idx}`}
                            onClick={(e) => { e.stopPropagation(); handlePrintRecuContributionAcquitte(doc); }} disabled={!paid}
                          ><Printer className="h-3.5 w-3.5" /> Imprimer recu</Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
